import {ImageMediaMetadata} from '../drive/types';

/** What the viewer's info panel shows about a photo, already formatted for display. */
export interface ExifSummary {
    camera?: string;
    lens?: string;
    exposure?: string;
    aperture?: string;
    iso?: string;
    focalLength?: string;
    dateTaken?: string;
    gps?: { lat: number; lon: number };
    dimensions?: { width: number; height: number };
}

interface DescribedTag {
    description?: string;
}

interface ValueTag {
    value?: unknown;
}

/** The parts of exifreader's expanded tags we read — structural, so tests can pass plain objects. */
export interface ExifTagsInput {
    exif?: {
        Make?: DescribedTag;
        Model?: DescribedTag;
        LensModel?: DescribedTag;
        ExposureTime?: DescribedTag;
        FNumber?: DescribedTag;
        ISOSpeedRatings?: DescribedTag;
        FocalLength?: DescribedTag;
        DateTimeOriginal?: DescribedTag;
        Orientation?: ValueTag;
    };
    gps?: { Latitude?: number; Longitude?: number };
    file?: { 'Image Width'?: ValueTag; 'Image Height'?: ValueTag };
}

/** At most one decimal, without trailing zeros: 2.8 → "2.8", 8.0 → "8". */
function formatNumber(value: number): string {
    return String(Number(value.toFixed(1)));
}

function positive(value: number | undefined): number | undefined {
    return value !== undefined && Number.isFinite(value) && value > 0 ? value : undefined;
}

function numberValue(tag: ValueTag | undefined): number | undefined {
    const value = Array.isArray(tag?.value) ? tag?.value[0] : tag?.value;
    return typeof value === 'number' ? value : undefined;
}

/** Drops the make when the model already repeats it: "NIKON CORPORATION" + "NIKON Z 6" → "NIKON Z 6". */
function joinCamera(make: string | undefined, model: string | undefined): string | undefined {
    const brand = make?.trim().split(/\s+/)[0].toLowerCase();
    if (model && brand && model.toLowerCase().startsWith(brand)) return model;
    return [make, model].filter(Boolean).join(' ') || undefined;
}

function orient(width: number | undefined, height: number | undefined, quarterTurned: boolean) {
    const w = positive(width);
    const h = positive(height);
    if (w === undefined || h === undefined) return undefined;
    return quarterTurned ? {width: h, height: w} : {width: w, height: h};
}

export function formatExposureTime(seconds: number): string {
    return seconds < 1 ? `1/${Math.round(1 / seconds)}` : `${formatNumber(seconds)} s`;
}

export function formatDimensions({width, height}: { width: number; height: number }): string {
    return `${width} × ${height}`;
}

/** EXIF "YYYY:MM:DD HH:MM:SS" → "YYYY.MM.DD. HH:MM:SS", like `formatDisplayDateTime`. Other strings are returned as they are. */
export function formatDateTaken(dateTime: string): string {
    return dateTime.replace(/^(\d{4}):(\d{2}):(\d{2}) /, '$1.$2.$3. ');
}

/** Whether there is anything besides the dimensions — dimensions alone don't count as EXIF. */
export function hasExifDetails(summary: ExifSummary): boolean {
    const {camera, lens, exposure, aperture, iso, focalLength, dateTaken, gps} = summary;
    return [camera, lens, exposure, aperture, iso, focalLength, dateTaken, gps].some((value) => value !== undefined);
}

/** Summary from the `imageMediaMetadata` Drive returns in folder listings (no download needed). */
export function summarizeDriveMetadata(meta: ImageMediaMetadata): ExifSummary {
    const exposure = positive(meta.exposureTime);
    const aperture = positive(meta.aperture);
    const focalLength = positive(meta.focalLength);
    const iso = positive(meta.isoSpeed);
    const latitude = meta.location?.latitude;
    const longitude = meta.location?.longitude;
    return {
        camera: joinCamera(meta.cameraMake, meta.cameraModel),
        lens: meta.lens || undefined,
        exposure: exposure !== undefined ? formatExposureTime(exposure) : undefined,
        aperture: aperture !== undefined ? `f/${formatNumber(aperture)}` : undefined,
        iso: iso !== undefined ? String(iso) : undefined,
        focalLength: focalLength !== undefined ? `${formatNumber(focalLength)} mm` : undefined,
        dateTaken: meta.time ? formatDateTaken(meta.time) : undefined,
        gps: latitude !== undefined && longitude !== undefined ? {lat: latitude, lon: longitude} : undefined,
        dimensions: orient(meta.width, meta.height, Math.abs(meta.rotation ?? 0) % 2 === 1),
    };
}

/** Summary from exifreader's expanded tags — the fallback for images Drive has no metadata for. */
export function summarizeExifTags(tags: ExifTagsInput): ExifSummary {
    const exif = tags.exif;
    const gps = tags.gps;
    const orientation = numberValue(exif?.Orientation);
    const dateTaken = exif?.DateTimeOriginal?.description;
    return {
        camera: joinCamera(exif?.Make?.description, exif?.Model?.description),
        lens: exif?.LensModel?.description,
        exposure: exif?.ExposureTime?.description,
        aperture: exif?.FNumber?.description,
        iso: exif?.ISOSpeedRatings?.description,
        focalLength: exif?.FocalLength?.description,
        dateTaken: dateTaken ? formatDateTaken(dateTaken) : undefined,
        gps: gps?.Latitude !== undefined && gps?.Longitude !== undefined
            ? {lat: gps.Latitude, lon: gps.Longitude}
            : undefined,
        dimensions: orient(
            numberValue(tags.file?.['Image Width']),
            numberValue(tags.file?.['Image Height']),
            orientation !== undefined && orientation >= 5 && orientation <= 8,
        ),
    };
}
